/**
 * Mapping notification.type → emplacements UX où afficher un badge rouge.
 *
 * Chaque entrée définit un emplacement par défaut (`default`) et, optionnellement,
 * des surcharges par rôle (`byRole`). Cela permet à une même notification
 * d'aboutir sur des écrans différents selon que le destinataire est un admin /
 * manager (côté gestion) ou un collaborateur / gestionnaire (côté "Mon profil").
 *
 * Format d'un emplacement :
 *   {
 *     menuTestid : `data-testid` du `<NavLink>` correspondant dans la sidebar
 *                  (cf. `Layout.jsx`),
 *     pageTabs   : liste de clés `{pageKey}.{tabValue}` correspondant à des
 *                  onglets / sous-onglets internes. Chaque clé reçoit le
 *                  compteur, ce qui permet d'afficher un badge à la fois sur
 *                  l'onglet parent et sur le sous-onglet concerné.
 *   }
 *
 * Tout type non listé ici ne déclenche pas de badge ciblé — il reste visible
 * via la cloche globale + le centre de notifications.
 */

// Helpers de DSL — réduisent le bruit visuel des entrées.
const tabs = (menuTestid, ...pageTabs) => ({ menuTestid, pageTabs });
const menu = (menuTestid) => ({ menuTestid });

// Emplacements récurrents
const SIDE_EQUIP_REQUESTS_COLLAB = tabs(
  "nav-equipements",
  "equipements.requests",
  "equipements.requests.collab",
);
const SIDE_EQUIP_REQUESTS_SITE = tabs(
  "nav-equipements",
  "equipements.requests",
  "equipements.requests.site",
);
const SIDE_EQUIP_RETURNS = tabs("nav-equipements", "equipements.returns");
const SIDE_EQUIP_SITES = tabs("nav-equipements", "equipements.sites");
const SIDE_EQUIP_COLLABS = tabs("nav-equipements", "equipements.collabs");
const SIDE_EQUIP_ALERTS = tabs("nav-equipements", "equipements.alerts");

const MINE_EQUIP = tabs("nav-mon-dossier", "mon-dossier.equip");
const MINE_SITE_REQ = tabs("nav-mon-dossier", "mon-dossier.site-req");
const MINE_LEAVES = tabs("nav-mon-dossier", "mon-dossier.leaves");
const MINE_PLANNING = tabs("nav-mon-dossier", "mon-dossier.planning");
const MINE_DOCS = tabs("nav-mon-dossier", "mon-dossier.docs");

// Rôles "gestionnaires" qui voient les écrans Matériels & équipements.
const STAFF = { admin: true, manager: true };

export const NOTIFICATION_BADGE_MAP = {
  // ============================================================
  // Matériels & équipements
  // ============================================================

  // Affectation collaborateur → notification reçue PAR le collaborateur.
  equipment_assigned: { default: MINE_EQUIP },
  equipment_assignment_cancelled: { default: MINE_EQUIP },

  // Demandes matériels (collab) — admin/manager reçoivent la demande,
  // le demandeur (collab) reçoit la décision.
  equipment_request: { default: SIDE_EQUIP_REQUESTS_COLLAB },
  equipment_request_decision: {
    byRole: { admin: SIDE_EQUIP_REQUESTS_COLLAB, manager: SIDE_EQUIP_REQUESTS_COLLAB },
    default: MINE_EQUIP,
  },

  // Demandes matériels (sites)
  equipment_site_request: { default: SIDE_EQUIP_REQUESTS_SITE },
  equipment_site_request_decision: {
    byRole: { admin: SIDE_EQUIP_REQUESTS_SITE, manager: SIDE_EQUIP_REQUESTS_SITE },
    default: MINE_SITE_REQ,
  },
  equipment_site_request_finalized: {
    byRole: { admin: SIDE_EQUIP_REQUESTS_SITE, manager: SIDE_EQUIP_REQUESTS_SITE },
    default: MINE_SITE_REQ,
  },
  // Matériel à acheminer sur site → receiver collab/gestionnaire.
  equipment_site_receive_pending: {
    byRole: { admin: SIDE_EQUIP_SITES, manager: SIDE_EQUIP_SITES },
    default: MINE_EQUIP,
  },
  equipment_site_received: { default: SIDE_EQUIP_SITES },

  // Restitutions (matériel collab)
  equipment_return_request: { default: MINE_EQUIP },
  equipment_return_requested: { default: SIDE_EQUIP_RETURNS },
  equipment_return_collab_signed: { default: SIDE_EQUIP_RETURNS },
  equipment_return_validated: {
    byRole: { admin: SIDE_EQUIP_RETURNS, manager: SIDE_EQUIP_RETURNS },
    default: MINE_EQUIP,
  },
  equipment_return_rejected_by_controller: {
    byRole: { admin: SIDE_EQUIP_RETURNS, manager: SIDE_EQUIP_RETURNS },
    default: MINE_EQUIP,
  },
  equipment_return_confirmed: {
    byRole: { admin: SIDE_EQUIP_RETURNS, manager: SIDE_EQUIP_RETURNS },
    default: MINE_EQUIP,
  },
  equipment_return_completed: {
    byRole: { admin: SIDE_EQUIP_RETURNS, manager: SIDE_EQUIP_RETURNS },
    default: MINE_EQUIP,
  },
  equipment_return_cancelled: {
    byRole: { admin: SIDE_EQUIP_RETURNS, manager: SIDE_EQUIP_RETURNS },
    default: MINE_EQUIP,
  },
  equipment_return_overdue: { default: SIDE_EQUIP_RETURNS },
  equipment_return_contract_end_reminder: { default: SIDE_EQUIP_RETURNS },

  // Restitutions sites
  equipment_site_return_contact_signed: { default: SIDE_EQUIP_RETURNS },
  equipment_site_return_validated: { default: SIDE_EQUIP_RETURNS },
  equipment_site_return_rejected_by_controller: { default: SIDE_EQUIP_RETURNS },

  // Stock
  equipment_stock_alert: { default: SIDE_EQUIP_ALERTS },
  equipment_assignment: { default: SIDE_EQUIP_COLLABS },

  // ============================================================
  // Indisponibilités / congés
  // ============================================================
  leave_request: { default: menu("nav-indispo") },
  leave_decision: {
    byRole: { admin: menu("nav-indispo"), manager: menu("nav-indispo") },
    default: MINE_LEAVES,
  },
  leave_cancelled: { default: menu("nav-indispo") },
  collaborateur_absent: { default: menu("nav-indispo") },
  collaborateur_marked_absent: {
    byRole: { admin: menu("nav-indispo"), manager: menu("nav-indispo") },
    default: MINE_LEAVES,
  },
  collaborateur_absence_cancelled: { default: menu("nav-indispo") },

  // ============================================================
  // Pointage / arrivées tardives
  // ============================================================
  late_clock_in: { default: menu("nav-pointage") },
  manual_clock_in_by_manager: {
    byRole: { admin: menu("nav-pointage"), manager: menu("nav-pointage") },
    default: MINE_PLANNING,
  },
  manual_clock_out_by_manager: {
    byRole: { admin: menu("nav-pointage"), manager: menu("nav-pointage") },
    default: MINE_PLANNING,
  },

  // ============================================================
  // Planification / agendas
  // ============================================================
  shift_assigned: {
    byRole: { admin: menu("nav-planning"), manager: menu("nav-planning") },
    default: MINE_PLANNING,
  },
  shift_assigned_replacement: {
    byRole: { admin: menu("nav-planning"), manager: menu("nav-planning") },
    default: MINE_PLANNING,
  },
  shift_planned: {
    byRole: { admin: menu("nav-planning"), manager: menu("nav-planning") },
    default: MINE_PLANNING,
  },
  shift_reminder: {
    byRole: { admin: menu("nav-planning"), manager: menu("nav-planning") },
    default: MINE_PLANNING,
  },
  shift_updated: {
    byRole: { admin: menu("nav-planning"), manager: menu("nav-planning") },
    default: MINE_PLANNING,
  },
  shift_cancelled: {
    byRole: { admin: menu("nav-planning"), manager: menu("nav-planning") },
    default: MINE_PLANNING,
  },
  shift_change_request: { default: menu("nav-planning") },
  shift_change_decision: {
    byRole: { admin: menu("nav-planning"), manager: menu("nav-planning") },
    default: MINE_PLANNING,
  },
  intervention_vacant: { default: menu("nav-historique-vacances") },
  intervention_pickup: { default: menu("nav-historique-vacances") },
  department_agenda_created: { default: menu("nav-agenda-departements") },
  department_agenda_updated: { default: menu("nav-agenda-departements") },

  // ============================================================
  // Bulletins de rémunération
  // ============================================================
  payslip_missing: {
    byRole: { admin: menu("nav-bulletins"), manager: menu("nav-bulletins") },
    default: MINE_DOCS,
  },
  payslip_missing_monthly: {
    byRole: { admin: menu("nav-bulletins"), manager: menu("nav-bulletins") },
    default: MINE_DOCS,
  },

  // ============================================================
  // Documents (Mon profil)
  // ============================================================
  document_uploaded: { default: MINE_DOCS },

  // ============================================================
  // Sécurité / audit
  // ============================================================
  password_reset_request: { default: menu("nav-audit") },
  password_reset_completed: { default: menu("nav-audit") },
};

/**
 * Retourne l'emplacement UX (menu + onglets) où afficher le badge rouge
 * pour une notification donnée, selon le rôle du destinataire.
 *
 * @param {string} notifType
 * @param {string} [role] — rôle du destinataire (admin/manager/gestionnaire/collaborateur)
 */
export function getBadgeLocation(notifType, role) {
  const entry = NOTIFICATION_BADGE_MAP[notifType];
  if (!entry) return null;
  if (role && entry.byRole && entry.byRole[role]) return entry.byRole[role];
  return entry.default || null;
}

// Réservé : ensemble des rôles "staff" (admin/manager) pour usage interne.
export const STAFF_ROLES = STAFF;
